/**
 * Multi-tenant subdomain utilities.
 * Handles subdomain detection and redirect logic for Jira/Slack-style tenant URLs.
 *
 * DOMAIN ENV CONFIGURATION
 * ------------------------
 * We support two environment variables:
 * - REACT_APP_DOMAIN_ENV or DOMAIN_ENV
 *     "production" | "qa" | "staging" | "dev" | ...
 * - REACT_APP_DOMAIN or DOMAIN
 *     base domain, e.g. "skeptics.ai"
 *
 * Behavior:
 * - When env is "production" or unset:
 *     root domain = "skeptics.ai"
 *     app        = "app.skeptics.ai"
 *     tenant     = "{slug}.skeptics.ai"
 *
 * - When env is non-production, e.g. "qa":
 *     root domain = "qa.skeptics.ai"
 *     app        = "app.qa.skeptics.ai"
 *     tenant     = "{slug}.qa.skeptics.ai"
 *
 * - For local/dev hosts (anything not ending in the configured root domain),
 *   we derive the root domain from the current hostname without hard-coding
 *   specific values like "localhost".
 */

const DOMAIN_ENV =
  (process.env.REACT_APP_DOMAIN_ENV || process.env.DOMAIN_ENV || '').toLowerCase();

// Base domain (no port); used for "effective domain" and subdomain detection
const BASE_DOMAIN = (process.env.REACT_APP_BASE_DOMAIN || 'skeptics.ai')
  .toLowerCase()
  .replace(/:.*$/, '');

/**
 * Effective root domain for the current env:
 * - Production (prod/production): REACT_APP_BASE_DOMAIN only, e.g. "skeptics.ai"
 * - Non-production: REACT_APP_DOMAIN_ENV.REACT_APP_BASE_DOMAIN, e.g. "qa.skeptics.ai"
 * Anything before this is the subdomain: <sub>.effectiveDomain
 */
function getEnvRootDomain() {
  const isProduction =
    !DOMAIN_ENV || DOMAIN_ENV === 'production' || DOMAIN_ENV === 'prod';
  if (isProduction) {
    return BASE_DOMAIN;
  }
  return `${DOMAIN_ENV}.${BASE_DOMAIN}`;
}

/**
 * True when hostname is exactly the effective root domain (no subdomain).
 * Root = REACT_APP_BASE_DOMAIN in production, or REACT_APP_DOMAIN_ENV.REACT_APP_BASE_DOMAIN otherwise.
 */
export function isRootHost(hostname) {
  const h = (hostname || '').toLowerCase();
  const envRoot = getEnvRootDomain();
  if (!h || !envRoot) return false;
  return h === envRoot || h === `www.${envRoot}`;
}

/**
 * True when hostname is the effective core/root host for current runtime host.
 * Works for configured domains and local/dev hosts (e.g. qa.localhost).
 */
export function isCoreHost(hostname) {
  const h = (hostname || '').toLowerCase();
  if (!h) return false;
  const effectiveRoot = getEffectiveRootDomain(h);
  if (!effectiveRoot) return false;
  return h === effectiveRoot || h === `www.${effectiveRoot}`;
}

/**
 * Resolve the "root" domain we should use for redirects, based on both:
 * - configured env/domain (for qa.skeptics.ai, staging.skeptics.ai, etc.)
 * - current hostname (for local/dev without hard-coding hostnames)
 */
function getEffectiveRootDomain(hostname) {
  const envRoot = getEnvRootDomain()?.toLowerCase();
  const lcHost = (hostname || '').toLowerCase();
  const domainEnv = DOMAIN_ENV?.toLowerCase();

  if (!lcHost || !envRoot) return envRoot;

  // If already on configured root domain or any of its subdomains
  if (
    lcHost === envRoot ||
    lcHost === `www.${envRoot}` ||
    lcHost === `app.${envRoot}` ||
    lcHost.endsWith(`.${envRoot}`)
  ) {
    return envRoot;
  }
  if(domainEnv === 'production'){
    return window.location.hostname;
  }

  // Handle localhost or single-label hosts
  const isLocalhost =
    lcHost === 'localhost' ||
    /^[0-9.]+$/.test(lcHost) || // IP address
    !lcHost.includes('.');

  if (isLocalhost) {
    if (!domainEnv || domainEnv === 'production') {
      return envRoot;
    }
    return `${domainEnv}.${lcHost}`;
  }

  // Fallback: derive root generically (last two segments)
  const parts = lcHost.split('.');
  if (parts.length >= 2) {
    return parts.slice(-2).join('.');
  }

  return envRoot;
}

/**
 * Subdomain (first label before effective domain) from hostname.
 * E.g. app.qa.skeptics.ai -> "app", qa.skeptics.ai -> "" (root).
 */
function getSubdomainLabel(hostname) {
  const h = (hostname || '').toLowerCase();
  const envRoot = getEnvRootDomain();
  if (!h || !envRoot || h === envRoot || h === `www.${envRoot}`) return '';
  const suffix = `.${envRoot}`;
  if (!h.endsWith(suffix)) return '';
  const prefix = h.slice(0, -suffix.length);
  const parts = prefix.split('.');
  return parts[0] || '';
}

/**
 * Extracts the subdomain from the current hostname.
 * @returns {string|null} The tenant subdomain (e.g., 'docyt') or null if main app or no subdomain
 */
export function getSubdomainFromHost() {
  const hostname = window.location.hostname;
  const subdomain = getSubdomainLabel(hostname);
  if (subdomain && subdomain !== 'www' && subdomain !== 'app') {
    return subdomain;
  }
  return null;
}

/**
 * Returns true when the user is on a tenant subdomain (e.g., docyt.localhost).
 * Excludes admin subdomain.
 * @returns {boolean}
 */
export function isTenantSubdomain() {
  const subdomain = getSubdomainFromHost();
  return subdomain !== null && subdomain !== 'admin';
}

/**
 * Gets the current subdomain from hostname (e.g., 'app', 'admin', 'docyt').
 * Based on effective domain: <sub>.REACT_APP_DOMAIN_ENV.REACT_APP_BASE_DOMAIN (non-prod) or <sub>.REACT_APP_BASE_DOMAIN (prod).
 * @returns {string}
 */
export function getCurrentSubdomain() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const sub = getSubdomainLabel(host);
  if (sub) return sub;

  // Generic fallback when host doesn't match configured env root domain.
  // Infer subdomain from host shape: <sub>.<domain>.
  if (!host || isRootHost(host)) return '';
  if (/^[0-9.]+$/.test(host)) return '';

  const labels = host.split('.');
  if (labels.length < 2) return '';

  const [firstLabel] = labels;
  if (firstLabel && firstLabel !== 'www') {
    return firstLabel;
  }

  return '';
}

/**
 * Returns true when on admin subdomain (admin.localhost or admin.skeptics.ai).
 * @returns {boolean}
 */
export function isAdminSubdomain() {
  return getCurrentSubdomain() === 'admin';
}

/**
 * Builds URL for a subdomain (same root domain, different subdomain).
 * @param {string} subdomain - Target subdomain (e.g., 'admin', 'app', 'docyt')
 * @param {string} path - Path to append (e.g., '/dashboard')
 * @returns {string} Full URL
 */
export function getSubdomainUrl(subdomain, path = '') {
  if (typeof window === 'undefined') return '';
  const { protocol, hostname, port } = window.location;

  // Decide which root domain to use for redirects based on env + current host
  const rootDomain = getEffectiveRootDomain(hostname);
  if(rootDomain === 'production'){
    return `${protocol}//${hostname}`;
  }
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
  let url = `${protocol}//${subdomain}.${rootDomain}`;
  if (port && port !== '80' && port !== '443') url += `:${port}`;
  return url + pathWithSlash;
}

/**
 * URL for app subdomain public paths (e.g. signin, signup).
 * Use this for all links and redirects to APP_SUBDOMAIN_PUBLIC_PATHS.
 * @param {string} path - Path (e.g. '/signin', '/signup')
 * @returns {string} Full app subdomain URL
 */
export function getAppPublicPathUrl(path) {
  const p = path && path.startsWith('/') ? path : `/${path || ''}`;
  return getSubdomainUrl('app', p);
}

/**
 * URL for root domain (no subdomain): Domain/path (e.g. skeptics.ai/contact, qa.skeptics.ai/home).
 * Use for all links and redirects to /, /contact, /home so they always go to the root host.
 * @param {string} path - Path (e.g. '/', '/contact', '/home')
 * @returns {string} Full root domain URL
 */
export function getRootDomainUrl(path) {
  if (typeof window === 'undefined') return '';
  const { protocol, hostname, port } = window.location;

  // Local development: stay on the same host.
  const rootDomain = getEffectiveRootDomain(hostname);
  if (rootDomain === 'production') {
    return `${protocol}//${hostname}` + (path && path.startsWith('/') ? path : `/${path || ''}`);
  }
  const pathWithSlash = path && path.startsWith('/') ? path : `/${path || ''}`;
  let url = `${protocol}//${rootDomain}`;
  if (port && port !== '80' && port !== '443') url += `:${port}`;
  return url + pathWithSlash;
}

/**
 * Redirects to a different subdomain (same root domain).
 * Use for any URL/domain change (admin, app, tenant).
 * @param {string} subdomain - Target subdomain (e.g., 'admin', 'app', 'docyt')
 * @param {string} path - Path to append (e.g., '/signin')
 * @param {Object} [authParams] - Auth params to pass in URL hash (token, username, tenantSlug, etc.)
 */
export function redirectToSubdomain(subdomain, path = '', authParams = {}) {
  let url = getSubdomainUrl(subdomain, path);
  if (url && Object.keys(authParams).length > 0) {
    const hash = new URLSearchParams(authParams).toString();
    url += `#${hash}`;
  }
  if (url) window.location.href = url;
}

